import { ChangeEvent, MouseEvent, useContext, useState } from "react";

import { useTranslation } from "react-i18next";

import { css } from "@emotion/react";

import {
    colors,
    VisibilityIcon,
    VisibilityOffIcon
} from "@dotnet/telematics-design-system";

import DialogActions from "components/Actions/dialogs/components/DialogActions";
import { BUTTON_TYPES, PASSWORD_TYPES } from "components/Auth/types/enums";
import ButtonDefault from "components/DesignComponents/ButtonDefault";
import Dialog from "components/DesignComponents/Dialog";
import InputField, {
    INPUT_FIELD_SIZES
} from "components/DesignComponents/InputField";
import PasswordValidation from "components/DesignComponents/PasswordValidation";
import {
    TOOLTIP_PLACEMENT,
    TooltipGeneral
} from "components/DesignComponents/Tooltips";
import AuthContext from "context/auth/authContext";
import { useApi } from "shared/hooks/useApi";
import { useMediaQueries } from "shared/hooks/useMediaQueries";
import { maxDialogWidth } from "shared/styles";
import { ENDPOINT_PATH_SEGMENTS, ENDPOINTS } from "shared/types/enums";
import { DialogHandler } from "shared/types/interfaces";

const ChangePasswordModal = ({ isOpen, close }: DialogHandler) => {
    const { gray500, gray600 } = colors;

    const { checkAuth } = useContext(AuthContext);

    const { t } = useTranslation();
    const { postData, handleResponse } = useApi();
    const { fromMd } = useMediaQueries();

    const [isLoading, setIsLoading] = useState(false);
    const [isEnabled, setIsEnabled] = useState(false);

    const [password, setPassword] = useState({
        [PASSWORD_TYPES.Current]: "",
        [PASSWORD_TYPES.NewPsw]: "",
        [PASSWORD_TYPES.Repeat]: ""
    });

    const [showPassword, setShowPassword] = useState({
        [PASSWORD_TYPES.Current]: false,
        [PASSWORD_TYPES.NewPsw]: false,
        [PASSWORD_TYPES.Repeat]: false
    });

    const toggleDisable = (value: boolean) => setIsEnabled(value);

    const handleValueChange = (e: ChangeEvent<HTMLInputElement>) =>
        setPassword({ ...password, [e.target.name]: e.target.value });

    const togglePswVisibility = (
        e: MouseEvent<HTMLSpanElement>,
        name: PASSWORD_TYPES
    ) => {
        e.preventDefault();
        setShowPassword({ ...showPassword, [name]: !showPassword[name] });
    };

    const handleSubmit = async () => {
        try {
            setIsLoading(true);

            const response = await postData(
                `${ENDPOINTS.Users}${ENDPOINT_PATH_SEGMENTS.ChangePassword}`,
                {
                    old_password: password[PASSWORD_TYPES.Current],
                    password: password[PASSWORD_TYPES.NewPsw]
                }
            );

            checkAuth();
            close();

            handleResponse(response);

            setPassword({
                [PASSWORD_TYPES.Current]: "",
                [PASSWORD_TYPES.NewPsw]: "",
                [PASSWORD_TYPES.Repeat]: ""
            });
        } catch (error) {
            handleResponse(error);
        }

        setIsLoading(false);
    };

    return (
        <Dialog
            data-testid="change-password-dialog"
            open={isOpen}
            close={close}
            isTitleSeparator
            isActionsSeparator
            title={t("Security##password##change password")}
            submit={handleSubmit}
            slotProps={{
                transition: {
                    onExited: () => {
                        setPassword({
                            [PASSWORD_TYPES.Current]: "",
                            [PASSWORD_TYPES.NewPsw]: "",
                            [PASSWORD_TYPES.Repeat]: ""
                        });

                        setShowPassword({
                            [PASSWORD_TYPES.Current]: false,
                            [PASSWORD_TYPES.NewPsw]: false,
                            [PASSWORD_TYPES.Repeat]: false
                        });
                    }
                }
            }}
            actions={
                <DialogActions
                    onClose={close}
                    isLoading={isLoading}
                    isReadyToConfirm={isEnabled}
                    confirmationButtonText={t("Button##change")}
                    cancelButtonTestId="change-password-cancel-button"
                    confirmationButtonTestId="change-password-confirm-button"
                />
            }
            css={css([
                {
                    "form > div > div + div:not(:last-of-type)": {
                        marginTop: "16px"
                    },

                    "& .change-psw-visibility": {
                        display: "flex",
                        cursor: "pointer",
                        color: gray500,
                        transition:
                            "color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms",

                        "&:hover": {
                            color: gray600
                        }
                    }
                },
                fromMd && maxDialogWidth
            ])}
        >
            <InputField
                data-testid="change-password-current-password-field"
                autoFocus
                fullWidth
                value={password[PASSWORD_TYPES.Current]}
                name={PASSWORD_TYPES.Current}
                labelLeft={t("Security##password##enter current password")}
                placeholder={t("General##enter here")}
                size={INPUT_FIELD_SIZES.Medium}
                type={
                    showPassword[PASSWORD_TYPES.Current] ? "text" : "password"
                }
                onChange={handleValueChange}
                readOnly={isLoading}
                iconRight={
                    <TooltipGeneral
                        disableTouchListener
                        placement={TOOLTIP_PLACEMENT.TopEnd}
                        title={
                            showPassword[PASSWORD_TYPES.Current]
                                ? t("Security##password##hide password")
                                : t("Security##password##show password")
                        }
                        css={css({
                            "& .MuiTooltip-tooltip": {
                                marginRight: "-6px"
                            }
                        })}
                    >
                        <ButtonDefault
                            onMouseDown={e =>
                                togglePswVisibility(e, PASSWORD_TYPES.Current)
                            }
                            className="change-psw-visibility"
                        >
                            {showPassword[PASSWORD_TYPES.Current] ? (
                                <VisibilityOffIcon />
                            ) : (
                                <VisibilityIcon />
                            )}
                        </ButtonDefault>
                    </TooltipGeneral>
                }
            />

            <InputField
                data-testid="change-password-new-password-field"
                fullWidth
                value={password[PASSWORD_TYPES.NewPsw]}
                name={PASSWORD_TYPES.NewPsw}
                labelLeft={t("Security##password##enter new password")}
                placeholder={t("General##enter here")}
                size={INPUT_FIELD_SIZES.Medium}
                type={showPassword[PASSWORD_TYPES.NewPsw] ? "text" : "password"}
                onChange={handleValueChange}
                readOnly={isLoading}
                iconRight={
                    <TooltipGeneral
                        disableTouchListener
                        placement={TOOLTIP_PLACEMENT.TopEnd}
                        title={
                            showPassword[PASSWORD_TYPES.NewPsw]
                                ? t("Security##password##hide password")
                                : t("Security##password##show password")
                        }
                        css={css({
                            "& .MuiTooltip-tooltip": {
                                marginRight: "-6px"
                            }
                        })}
                    >
                        <ButtonDefault
                            onMouseDown={e =>
                                togglePswVisibility(e, PASSWORD_TYPES.NewPsw)
                            }
                            className="change-psw-visibility"
                        >
                            {showPassword[PASSWORD_TYPES.NewPsw] ? (
                                <VisibilityOffIcon />
                            ) : (
                                <VisibilityIcon />
                            )}
                        </ButtonDefault>
                    </TooltipGeneral>
                }
            />

            <InputField
                data-testid="change-password-repeat-password-field"
                fullWidth
                value={password[PASSWORD_TYPES.Repeat]}
                name={PASSWORD_TYPES.Repeat}
                labelLeft={t("Security##password##repeat new password")}
                placeholder={t("General##enter here")}
                size={INPUT_FIELD_SIZES.Medium}
                type={showPassword[PASSWORD_TYPES.Repeat] ? "text" : "password"}
                onChange={handleValueChange}
                readOnly={isLoading}
                iconRight={
                    <TooltipGeneral
                        disableTouchListener
                        placement={TOOLTIP_PLACEMENT.TopEnd}
                        title={
                            showPassword[PASSWORD_TYPES.Repeat]
                                ? t("Security##password##hide password")
                                : t("Security##password##show password")
                        }
                        css={css({
                            "& .MuiTooltip-tooltip": {
                                marginRight: "-6px"
                            }
                        })}
                    >
                        <ButtonDefault
                            onMouseDown={e =>
                                togglePswVisibility(e, PASSWORD_TYPES.Repeat)
                            }
                            className="change-psw-visibility"
                        >
                            {showPassword[PASSWORD_TYPES.Repeat] ? (
                                <VisibilityOffIcon />
                            ) : (
                                <VisibilityIcon />
                            )}
                        </ButtonDefault>
                    </TooltipGeneral>
                }
                css={css({
                    marginBottom: "24px"
                })}
            />

            <PasswordValidation
                name={BUTTON_TYPES.NavBarChange}
                currentPsw={password[PASSWORD_TYPES.Current]}
                newPsw={password[PASSWORD_TYPES.NewPsw]}
                repeatPsw={password[PASSWORD_TYPES.Repeat]}
                onRulesMet={toggleDisable}
            />
        </Dialog>
    );
};

export default ChangePasswordModal;
